import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  currentPrivacyConsent,
  individualSignerRole,
  IndividualWithResult,
  privacyConsentDisplayStatus,
  SignerRoleTypes,
} from '@portal-workspace/grow-shared-library';
import { SignerRolePipe } from '../../pipes/signer-role.pipe';
import { TagBoxComponent } from '../message-box/tag-box.component';
import {
  describeRole,
  identityMismatches,
  keptRecordLosesSignedConsent,
  kycDetailsMismatch,
  missingDetailsAfterMerge,
  MergeProfileRow,
  MergeSalesforceContact,
  pickDefaultSurvivor,
  previewMergedIndividual,
  previewProfileMerge,
  salesforceContactsInMerge,
} from './merge-individuals';

export interface MergeIndividualsDialogData {
  /** Every live individual on the application. */
  individuals: IndividualWithResult[];
  /** The person the staff member opened the dialog from; starts ticked. */
  initialIndividualId: number;
  /** Ids of the individuals that have an ID document on file. */
  individualsWithIdDocument?: number[];
}

export interface MergeIndividualsDialogResult {
  cancelled: boolean;
  survivorIndividualId?: number;
  mergedIndividualIds?: number[];
}

interface StatusIcon {
  icon: string;
  styleClass: string;
  tooltip: string;
}

interface MergeCandidateView {
  id: number;
  name: string;
  initials: string;
  dob: string;
  signerRole: SignerRoleTypes;
  isPrimaryContact: boolean;
  kyc: StatusIcon | null;
  consent: StatusIcon;
  roles: string[];
}

interface MergeResultView extends MergeCandidateView {
  /** Company roles after the merge; `added` marks a company the kept record did not hold before. */
  companyRoles: { label: string; added: boolean }[];
  /** Details the table will still flag as missing; empty unless the kept record is flagged. */
  missingDetails: string[];
  needsNewConsent: boolean;
}

// Same icons and colours as the Status column on the KYC/AML individuals table. WAITING shows nothing there.
const KYC_ICONS: Record<string, StatusIcon> = {
  RECEIVED_COMPLETE: { icon: 'mdi-check-circle-outline', styleClass: 'ok', tooltip: 'Verification successful' },
  RECEIVED_PENDING_REVIEW: { icon: 'mdi-progress-alert', styleClass: 'pending-review', tooltip: 'Under review' },
  RECEIVED_INCOMPLETE: { icon: 'mdi-progress-question', styleClass: 'incomplete', tooltip: 'Verification incomplete' },
  RECEIVED_FAILED: { icon: 'mdi-alert-circle-outline', styleClass: 'error', tooltip: 'KYC verification failed' },
  RECEIVED_ERROR: { icon: 'mdi-alert-circle-outline', styleClass: 'error', tooltip: 'System error' },
  SMS_SENT: { icon: 'mdi-timer-sand', styleClass: 'waiting', tooltip: 'Verification SMS sent' },
};

const fullName = (individual: IndividualWithResult): string =>
  [individual.givenName, individual.middleName, individual.surname].filter(Boolean).join(' ') || 'Unnamed';

const formatDob = (value: string | null | undefined): string => {
  const [year, month, day] = (value ?? '').slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : '';
};

const entityKey = (entityId: number | null | undefined): string => (entityId == null ? 'none' : String(entityId));

/** The server merges one person into another per request; more duplicates take separate merges. */
const MAX_PARTICIPANTS = 2;

@Component({
  selector: 'merge-individuals-dialog',
  templateUrl: './merge-individuals.dialog.html',
  styleUrls: ['./merge-individuals.dialog.scss'],
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatCheckboxModule, MatRadioModule, MatTooltipModule, SignerRolePipe, TagBoxComponent],
})
export class MergeIndividualsDialog {
  candidates: MergeCandidateView[];
  participantIds: number[];
  survivorId: number | null;
  profilePreview: MergeProfileRow[] = [];
  result: MergeResultView | null = null;
  salesforceContacts: MergeSalesforceContact[] = [];
  /** Why each record being discarded does not look like the kept person, keyed by individual id. */
  mismatchReasons: Record<number, string[]> = {};
  /** What the kept KYC result was verified for that the kept record does not match. */
  kycMismatch: string | null = null;
  /** Reset on every change to the selection, so a confirmation never covers people it was not given for. */
  confirmedSamePerson = false;
  private survivorChosenByUser = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MergeIndividualsDialogData,
    private matDialogRef: MatDialogRef<MergeIndividualsDialog, MergeIndividualsDialogResult>,
  ) {
    this.candidates = data.individuals.map(individual => this.toView(individual));
    this.participantIds = [data.initialIndividualId];
    this.survivorId = null;
    this.refreshSurvivorAndPreview();
  }

  get canSubmit(): boolean {
    return this.participantIds.length === MAX_PARTICIPANTS && this.survivorId !== null && this.participantIds.includes(this.survivorId)
      && (!this.hasMismatch || this.confirmedSamePerson);
  }

  get hasIdentityMismatch(): boolean {
    return Object.keys(this.mismatchReasons).length > 0;
  }

  get hasMismatch(): boolean {
    return this.hasIdentityMismatch || this.kycMismatch !== null;
  }

  /** Ticked, and not the kept record: the ones the merge retires. */
  isDiscarded(id: number): boolean {
    return this.isParticipant(id) && this.participantIds.length >= 2 && id !== this.survivorId;
  }

  /** Initials of the ticked records that fold into the kept one, for the header. */
  get retiringInitials(): string[] {
    return this.candidates
      .filter(candidate => this.isParticipant(candidate.id) && candidate.id !== this.survivorId)
      .map(candidate => candidate.initials);
  }

  get survivorInitials(): string {
    return this.candidates.find(candidate => candidate.id === this.survivorId)?.initials ?? '';
  }

  isParticipant(id: number): boolean {
    return this.participantIds.includes(id);
  }

  /** Unticked while two records are already ticked, so it cannot join this merge. */
  isLocked(id: number): boolean {
    return !this.isParticipant(id) && this.participantIds.length >= MAX_PARTICIPANTS;
  }

  onToggleParticipant(id: number, event: MatCheckboxChange) {
    this.setParticipant(id, event.checked);
  }

  onRowClick(id: number) {
    this.setParticipant(id, !this.isParticipant(id));
  }

  onSelectSurvivor(event: MatRadioChange) {
    this.survivorId = event.value;
    this.survivorChosenByUser = true;
    this.refreshSurvivorAndPreview();
  }

  onConfirmSamePerson(event: MatCheckboxChange) {
    this.confirmedSamePerson = event.checked;
  }

  onCancel() {
    this.matDialogRef.close({ cancelled: true });
  }

  onSubmit() {
    if (!this.canSubmit) return;
    this.matDialogRef.close({
      cancelled: false,
      survivorIndividualId: this.survivorId!,
      mergedIndividualIds: this.participantIds.filter(id => id !== this.survivorId),
    });
  }

  private setParticipant(id: number, checked: boolean) {
    if (checked && this.isLocked(id)) return;
    this.participantIds = checked
      ? [...new Set([...this.participantIds, id])]
      : this.participantIds.filter(existing => existing !== id);
    this.refreshSurvivorAndPreview();
  }

  private participants(): IndividualWithResult[] {
    return this.data.individuals.filter(individual => this.participantIds.includes(individual.id));
  }

  private refreshSurvivorAndPreview() {
    const participants = this.participants();
    const stillValid = this.survivorChosenByUser && this.survivorId !== null && this.participantIds.includes(this.survivorId);
    if (!stillValid) {
      this.survivorChosenByUser = false;
      this.survivorId = pickDefaultSurvivor(participants)?.id ?? null;
    }
    const survivor = participants.find(individual => individual.id === this.survivorId);
    const others = participants.filter(individual => individual.id !== survivor?.id);
    this.confirmedSamePerson = false;
    this.mismatchReasons = {};
    this.kycMismatch = null;
    if (survivor) {
      others.forEach(other => {
        const reasons = identityMismatches(survivor, other);
        if (reasons.length) this.mismatchReasons[other.id] = reasons;
      });
    }
    if (survivor && others.length) {
      this.kycMismatch = kycDetailsMismatch(survivor, others);
    }
    if (!survivor || !others.length) {
      this.profilePreview = [];
      this.result = null;
      this.salesforceContacts = [];
      return;
    }
    this.profilePreview = previewProfileMerge(survivor, others).map(row => row.label === 'Date of birth'
      ? { ...row, result: formatDob(row.result) || row.result, discarded: row.discarded.map(value => formatDob(value) || value) }
      : row);
    this.salesforceContacts = salesforceContactsInMerge(survivor, others);
    const merged = previewMergedIndividual(survivor, others);
    const heldBefore = new Set((survivor.roles ?? []).map(role => entityKey(role.entityId)));
    const mergedView = this.toView(merged);
    this.result = {
      ...mergedView,
      kyc: this.kycMismatch ? KYC_ICONS['RECEIVED_PENDING_REVIEW'] : mergedView.kyc,
      missingDetails: survivor.needsAttention
        ? missingDetailsAfterMerge(survivor, others, participants.some(person => this.data.individualsWithIdDocument?.includes(person.id)))
        : [],
      needsNewConsent: keptRecordLosesSignedConsent(survivor, others),
      companyRoles: (merged.roles ?? []).map(role => ({ label: describeRole(role), added: !heldBefore.has(entityKey(role.entityId)) })),
    };
  }

  private toView(individual: IndividualWithResult): MergeCandidateView {
    const name = fullName(individual);
    const consent = currentPrivacyConsent(individual.privacyConsent);
    const consentStatus = consent ? privacyConsentDisplayStatus(consent) : null;
    return {
      id: individual.id,
      name,
      initials: name.split(/[\s_]+/).filter(Boolean).slice(0, 2).map(part => part[0].toUpperCase()).join(''),
      dob: formatDob(individual.dateOfBirth),
      signerRole: individualSignerRole(individual),
      isPrimaryContact: !!individual.isPrimaryContact,
      kyc: KYC_ICONS[individual.result?.status ?? ''] ?? null,
      consent: consentStatus
        ? { icon: consentStatus.icon, styleClass: consentStatus.styleKey, tooltip: consentStatus.label }
        : { icon: 'mdi-help-circle-outline', styleClass: 'none', tooltip: 'No consent form generated yet' },
      roles: (individual.roles ?? []).map(describeRole),
    };
  }
}
